import {typeOf} from './utils'

/**
 * Collection acts like the standard Map object but with some additional methods.
 * It allows you to have tree from Map objects and to operate easily with them.
 *
 * Here are some tips:
 *
 * You can access some tree element either by calling:
 *
 * ```js
 * myCollection.get('item1').get('sub-item').get(0)
 * ```
 *
 * or with:
 *
 * ```js
 * myCollection.getDeep('item1.sub-item.+0')
 * ```
 */
export default class Collection
{
    /* ----------------- Map methods ----------------- */
    /* See: https://github.com/babel/babel/issues/6453 */

    constructor(iterable = []) {
        this.__map = new Map(iterable)
        this.size = this.__map.size
    }

    [Symbol.iterator]() {
        return this.__map[Symbol.iterator](arguments)
    }

    forEach(callback, thisArg = undefined) {
        this.__map.forEach(callback, thisArg)
    }

    clear() {
        return this.__map.clear()
    }

    delete(key) {
        return this.__map.delete(key)
    }

    entries() {
        return this.__map.entries()
    }

    get(key) {
        return this.__map.get(key)
    }

    has(key) {
        return this.__map.has(key)
    }

    keys() {
        return this.__map.keys()
    }

    values() {
        return this.__map.values()
    }

    set(key, value) {
        return this.__map.set(key, value)
    }

    /* --------------- end of Map methods --------------- */

    /**
     * Create a collection from an object
     *
     * @example
     * let collection = Collection.createFromObject({
     *     a : 2,
     *     b : {
     *         c : 4,
     *         d : [1, 2, 3],
     *         g : null
     *     }
     * })
     *
     * @param object
     * @returns {Collection}
     */
    static createFromObject(object)
    {
        function generate (obj)
        {
            let collection = []

            for (let key in obj)
            {
                if(obj.hasOwnProperty && !obj.hasOwnProperty(key)) continue

                if( typeOf(obj) === 'array' )
                {
                    if( isNaN(key) || key.indexOf('.') !== -1 )
                    {
                        throw new Error('Error in key: "'+ key +'". You cannot use array with string keys! Please use object instead.')
                    }

                    key = parseInt(key)
                }

                collection.push([
                    key
                    ,
                    (typeOf(obj[key]) === 'object' || typeOf(obj[key]) === 'array')
                        ? new Collection(generate(obj[key]))
                        : obj[key]
                ])
            }

            return collection
        }

        return new Collection(generate(object))
    }

    /**
     * Get tree value by a given key
     *
     * If you want to cast some key to Number than use + sign infront of the key.
     * This is because Map object will not return .get('1') if the key is integer.
     * Example: getDeep('test.items.+4.other')
     *
     * @param key Dot separated key path
     * @param otherwise Default value if wanted key path is undefined
     * @returns {*}
     */
    getDeep (key, otherwise = null)
    {
        let result = key.split('.').reduce(
            (object, index) => {
                index = this._clearIndex(index)

                if( object instanceof Map || object instanceof Collection )
                {
                    return object.get(index)
                }

                if( typeof object === 'undefined' ) {
                    return
                }

                return object[index]
            },
            this
        )

        return (undefined !== result) ? result : otherwise
    }

    /**
     * Check if a tree key path exist
     *
     * @param key Dot separated key path
     * @returns {boolean}
     */
    hasDeep(key)
    {
        return (undefined !== this.getDeep(key, undefined))
    }

    /**
     * Set value of nested tree element
     *
     * @param key key Dot separated key path
     * @param value New value
     * @param onlyOnExisting If set to true, method will throw an exception if the key do not exist
     * @returns {*}
     */
    setDeep(key, value, onlyOnExisting = false)
    {
        let keys = key.split('.')
        let lastKey = keys[keys.length-1]

        keys.reduce(
            (object, indexRaw) => {

                let index = this._clearIndex( indexRaw )

                // --- set the last the value of the last key of the given string path  ---
                if(indexRaw === lastKey)
                {
                    if( object instanceof Map || object instanceof Collection )
                    {
                        if(!onlyOnExisting || onlyOnExisting && object.has( index )) {
                            object.set( index, value )
                        }
                        else {
                            throw new Error('"'+ key +'" is undefined. You cannot set value to it.')
                        }
                    }
                    else if (onlyOnExisting && undefined === object[index]) {
                        throw new Error('"'+ key +'" is undefined. You cannot set value to it.')
                    }
                    else {
                        object[index] = value
                    }

                    return
                }

                // --- Return or Create and Return current target path ---

                // handle Map objects
                if( object instanceof Map || object instanceof Collection )
                {
                    let current = object.get( index )

                    if(undefined === current)
                    {
                        if (onlyOnExisting)
                            throw new Error('"'+ key +'" is undefined. You cannot set value to it.')

                        object.set(index, new Collection())
                        return object.get( index )
                    }
                    return current
                }

                // handle plain objects or arrays
                let current = object[ index ]
                if(undefined === current)
                {
                    if (onlyOnExisting)
                        throw new Error('"'+ key +'" is undefined. You cannot set value to it.')

                    object[ index ] = new Collection()
                    return object[ index ]
                }
                return current
            },
            this
        )
    }

    /**
     * Convert tree of Map objects to plain object or array
     *
     * @returns {object|array}
     */
    toObject ()
    {
        let keys = Array.from(this.keys())

        const object = (!keys.length || undefined !== keys.find(Number.isInteger)) ? [] : Object.create(null)

        for (const [key, value] of this.entries())
        {
            object[key] = ( value instanceof Map || value instanceof Collection )
                ? value.toObject()
                : value
        }

        return object
    }

    /**
     * Convert tree of Map objects to plain JSON string
     *
     * @return {string} Json string
     */
    toJson ()
    {
        return JSON.stringify(this.toObject())
    }

    /////////////////////////////////////////////

    /**
     * Clear index from + int casting char.
     *
     * @param index String key
     * @returns {Number} ParsedInt or the original value
     * @private
     */
    _clearIndex (index)
    {
        return (index.indexOf('+') === 0)
            ? parseInt(index.substr(1))
            : index
    }
}
